import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import {generateValidationSchemaFromAttributes, getUserResources, LoadingScreen} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import WizardStepper from '../Wizard/WizardStepper';
import WizardNavigation from '../Wizard/WizardNavigation';
import { WizardStepConfig } from '../Wizard/types';
import ConnectionStep from './Steps/ConnectionStep';
import SpecificationsStep from './Steps/SpecificationsStep';
import ServiceDataStep from './Steps/ServiceDataStep';
import SummaryStep from './Steps/SummaryStep';
import ConnectionUnavailableView from './ConnectionUnavailableView';
import { WizardFormData, StepCategory } from './types';
import { InstalledBaseNodeListTdas } from '../../graphql/queries/installedBaseNodeListTdas';
import { AssetNameplateBasicInfo } from '../../graphql/queries/assetNameplateBasicInfo';
import { AssetCustomer } from '../../graphql/queries/assetCustomer';
import { CreateIoTConnection } from '../../graphql/mutations/createIoTConnection';
import { UpdateIoTConnection } from '../../graphql/mutations/updateIoTConnection';
import { ValidateIoTConnectionInput } from '../../graphql/queries/validateIoTConnectionInput';
import { AssetIoTConnectionEditValues } from '../../graphql/queries/assetIoTConnectionEditValues';
import { AssetLocationIotSystem } from '../../graphql/queries/assetLocationIotSystem';
import { InstalledBaseNodeTypeDefinitionIndex, TypeDefinitionAttribute } from '../../codegen/graphql';
import { FormikHelpersRef } from '../../types';
import { decorateConnectionAttributes, decorateServiceDataAttributes, decorateSpecificationAttributes, getReadOnlyServiceDataIndexes, isGasBallastEnabled, SERVICE_DATA_COUNTERPARTS } from '../../utils/serviceDataValidation';
import { isPlcSystemType } from '../../config/systemTypes';
import { useIotAttachGate } from '../../hooks/useIotAttachGate';
import { isSlaEnabled } from '../../utils/slaEnabled';
import { isNetworkScanPassed } from '../../config/networkScanConfig';
import { describeNetworkScanSource } from '../NetworkScanGateReason';

const HIDDEN_ATTRIBUTE_INDICES = ['assetUrn', 'serialNumber', 'productCode', 'productName', 'manufacturerName', 'customerId', 'customerName'];

// Optional IoTConnection{Create,Update}AttributesInput fields. The IoT backend
// expects every field on every request; a missing/empty optional is sent as an
// explicit null (which unsets the value on the IoT side).
const OPTIONAL_INPUT_FIELDS = [
    'connectedToGateway',
    'gatewayImei',
    'oilCategory',
    'oilType',
    'gasBallast',
    'gasBallastType',
    'operatingPressure',
    'catalogVariant',
    'acousticEnclosure',
    'registeredLastServiceMaintenance',
    'registeredLastOverhaulReManufacturing',
    'registeredRunningHoursSinceLastServiceMaintenance',
    'registeredRunningHoursSinceLastOverhaulReManufacturing',
    'lastServiceMaintenance',
    'lastOverhaulReManufacturing',
    'runningHoursSinceLastServiceMaintenance',
    'runningHoursSinceLastOverhaulReManufacturing',
];

interface Props {
    assetLocationId: string;
    installedBaseNodeId: string;
    mode?: 'create' | 'edit';
    onIoTConnectionCreated?: () => void;
}

const STEP_CATEGORIES: StepCategory[] = ['connection', 'specifications', 'serviceData'];

const AssetConnectionWizard = ({ assetLocationId, installedBaseNodeId, mode = 'create', onIoTConnectionCreated }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { keycloak } = useKeycloak();
    const canAdministrateCustomers = getUserResources(keycloak.token, 'otto_crm').includes('administrateCustomers');
    const isEditMode = mode === 'edit';

    const [activeStep, setActiveStep] = useState(0);
    const [highestStepReached, setHighestStepReached] = useState(0);
    const [formData, setFormData] = useState<WizardFormData>({
        connection: {},
        specifications: {},
        serviceData: {},
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    // Set when Finish found invalid saved data and navigated back to the offending
    // step: triggers validate() once that step's form has mounted, so the errors show.
    const [validateStepOnMount, setValidateStepOnMount] = useState(false);

    const formikRef = useRef<FormikHelpersRef | null>(null);

    // Mutation for creating IoT connection
    const [createIoTConnection] = useMutation(CreateIoTConnection);
    const [updateIoTConnection] = useMutation(UpdateIoTConnection);

    // Lazy query for validating IoT connection input
    const [validateConnection] = useLazyQuery(ValidateIoTConnectionInput, {
        fetchPolicy: 'network-only',
    });

    // Current connection values: prefill source for edit mode
    const { data: editValuesData, loading: editValuesLoading, error: editValuesError, refetch: refetchEditValues } = useQuery(AssetIoTConnectionEditValues, {
        variables: { id: assetLocationId },
        skip: !isEditMode,
        fetchPolicy: 'network-only',
    });
    const editConnection = _.get(editValuesData, 'assetLocation.iotConnection');

    // Queries for hidden field values (auto-populated from asset data)
    const { data: assetData, loading: assetLoading, error: assetError, refetch: refetchAsset } = useQuery(AssetNameplateBasicInfo, {
        variables: { id: assetLocationId },
    });

    const { data: customerData, loading: customerLoading, error: customerError, refetch: refetchCustomer } = useQuery(AssetCustomer, {
        variables: { id: assetLocationId },
    });

    // Pre-check source: the IoT system the asset is installed under (create mode only)
    const { data: systemData, loading: systemLoading, error: systemError, refetch: refetchSystem } = useQuery(AssetLocationIotSystem, {
        variables: { id: assetLocationId },
        skip: isEditMode,
    });

    // IoT attach gate (IBM-1039): the backend folds the effective network scan
    // into AssetLocation.canAttachIotConnection. Create mode only — editing an
    // existing connection is not a new attach. Not sent while the flag is off.
    const iotGate = useIotAttachGate({ kind: 'assetLocation', id: assetLocationId, skip: isEditMode });

    const productNodeId = _.get(assetData, 'assetLocation.asset.product.id');

    // Query for TDA attributes
    const { data, loading: tdaLoading, error: tdaError, refetch: refetchTdas } = useQuery(InstalledBaseNodeListTdas, {
        variables: {
            typeDefinitionIndex: InstalledBaseNodeTypeDefinitionIndex.IOT_CONNECTION_CREATE,
            options: { productNodeId },
        },
        skip: !productNodeId,
    });

    const allAttributes: TypeDefinitionAttribute[] = useMemo(() => {
        return _.get(data, 'installedBaseNodeList.typeDefinitionAttributeList.typeDefinitionAttributes', []) || [];
    }, [data]);

    // Pre-check: required values for IoTConnectionCreateAttributesInput that are
    // auto-populated from asset/customer data (all non-nullable in the schema)
    const missingRequiredFields = useMemo(() => {
        const requiredValues: Array<[unknown, string]> = [
            [_.get(assetData, 'assetLocation.asset.urn'), t('Asset URN')],
            [_.get(assetData, 'assetLocation.asset.serialNumber'), t('Serial number')],
            [_.get(assetData, 'assetLocation.asset.product.name'), t('Product name')],
            [_.get(assetData, 'assetLocation.asset.product.manufacturer.name'), t('Manufacturer name')],
            [_.get(customerData, 'assetLocation.owner.id'), t('Customer')],
            [_.get(customerData, 'assetLocation.owner.name'), t('Customer name')],
            [_.get(customerData, 'assetLocation.owner.address.country.countryCode'), t('Customer address country')],
            [_.get(customerData, 'assetLocation.owner.region.key'), t('Customer region')],
            [_.get(customerData, 'assetLocation.owner.address.timezone.key'), t('Customer address timezone')],
        ];
        return requiredValues
            .filter(([value]) => value === null || value === undefined || value === '')
            .map(([, label]) => label);
    }, [assetData, customerData, t]);

    const productAllowsIot = !!_.get(assetData, 'assetLocation.asset.product.iotConfiguration.id');

    // Filter attributes by category (excluding hidden auto-populated fields)
    const getAttributesForCategory = useCallback((category: StepCategory): TypeDefinitionAttribute[] => {
        return allAttributes.filter((attr: TypeDefinitionAttribute) =>
            attr.categories?.includes(category) && !HIDDEN_ATTRIBUTE_INDICES.includes(attr.index)
        );
    }, [allAttributes]);

    // In edit mode a registered service value is locked once its telemetry-driven
    // counterpart is set on the connection (read-only, exempt from validation).
    const readOnlyServiceDataIndexes = useMemo(
        () => (isEditMode ? getReadOnlyServiceDataIndexes(editConnection) : []),
        [isEditMode, editConnection]
    );

    // Service data attributes carry client-side validations (frontendConfig.validations)
    // interpreted by the shared TDAForm; the backend sends none for these fields yet.
    const serviceDataAttributes = useMemo(
        () => decorateServiceDataAttributes(getAttributesForCategory('serviceData'), t, readOnlyServiceDataIndexes),
        [getAttributesForCategory, t, readOnlyServiceDataIndexes]
    );

    // The gateway IMEI is gated by the "connected to gateway" checkbox
    const connectionAttributes = useMemo(
        () => decorateConnectionAttributes(getAttributesForCategory('connection'), t),
        [getAttributesForCategory, t]
    );

    const getStepAttributes = useCallback((category: StepCategory): TypeDefinitionAttribute[] => {
        if (category === 'serviceData') return serviceDataAttributes;
        if (category === 'connection') return connectionAttributes;
        // Mirror the SpecificationsStep's live filtering (there based on the current
        // checkbox, here on the saved values), so the Finish re-validation never
        // demands the hidden gas ballast type.
        return decorateSpecificationAttributes(
            getAttributesForCategory(category),
            isGasBallastEnabled(formData.specifications),
            t
        );
    }, [serviceDataAttributes, connectionAttributes, getAttributesForCategory, formData.specifications, t]);

    // Seed the form once with the connection's current values (edit mode)
    const editSeededRef = useRef(false);
    useEffect(() => {
        if (!isEditMode || editSeededRef.current || !editConnection || allAttributes.length === 0) return;
        editSeededRef.current = true;

        const values: Record<string, unknown> = _.pickBy({
            imei: _.get(editConnection, 'gateway.imei'),
            connectedToGateway: Boolean(_.get(editConnection, 'gateway.gateway')),
            gatewayImei: _.get(editConnection, 'gateway.gateway.imei'),
            ..._.pick(editConnection, [
                'assetType',
                'operatingFrequency',
                'operatingPressure',
                'oilCategory',
                'oilType',
                'gasBallast',
                'gasBallastType',
                'catalogVariant',
                'acousticEnclosure',
            ]),
            ..._.pick(editConnection, Object.keys(SERVICE_DATA_COUNTERPARTS)),
        }, (value) => value !== null && value !== undefined);

        // Distribute by the actual TDA categories — an index can live in a different
        // step than expected (e.g. assetType is a connection attribute).
        const remaining = { ...values };
        const forCategory = (category: StepCategory) => {
            const indexes = getAttributesForCategory(category).map((attr) => attr.index);
            const categoryValues = _.pick(remaining, indexes);
            indexes.forEach((index) => { delete remaining[index]; });
            return categoryValues;
        };
        const connection = forCategory('connection');
        const serviceData = forCategory('serviceData');
        // Values without an own form field ride along in specifications: Formik keeps
        // non-rendered initial values, so they still reach the mutation input.
        const specifications = { ...forCategory('specifications'), ...remaining };

        setFormData({ connection, specifications, serviceData });
    }, [isEditMode, editConnection, allAttributes, getAttributesForCategory]);

    const wizardSteps: WizardStepConfig[] = useMemo(() => [
        {
            id: 0,
            title: t('Connection'),
            subtitle: t('IMEI & Gateway'),
            category: 'connection' as StepCategory,
        },
        {
            id: 1,
            title: t('Specifications'),
            subtitle: t('Technical Information'),
            category: 'specifications' as StepCategory,
        },
        {
            id: 2,
            title: t('Service Data'),
            subtitle: t('Service Information'),
            category: 'serviceData' as StepCategory,
        },
        {
            id: 3,
            title: t('Summary'),
            subtitle: '',
        },
    ], [t]);

    const validateIoTInput = useCallback(async (values: Record<string, unknown>): Promise<boolean> => {
        // Editing without touching either IMEI needs no server check — the stored
        // values are the ones the validator would flag as "already in use".
        if (isEditMode) {
            const unchanged =
                ((values.imei as string) || '') === (_.get(editConnection, 'gateway.imei') || '') &&
                ((values.gatewayImei as string) || '') === (_.get(editConnection, 'gateway.gateway.imei') || '');
            if (unchanged) return true;
        }

        setIsValidating(true);
        try {
            // The serial number is only checked when connecting; when editing an
            // existing connection it would be rejected as already in use.
            const serialNumber = isEditMode
                ? undefined
                : _.get(assetData, 'assetLocation.asset.serialNumber', '') || undefined;
            const imei = (values.imei as string) || undefined;
            const gatewayImei = (values.gatewayImei as string) || undefined;

            const { data: validationData } = await validateConnection({
                variables: {
                    input: { serialNumber, imei, gatewayImei },
                },
            });

            const result = validationData?.validateIoTConnectionInput;

            if (!result?.isValid) {
                toast.error(result?.error?.message || t('Validation failed'));
                return false;
            }

            return true;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('An unexpected error occurred');
            toast.error(errorMessage);
            return false;
        } finally {
            setIsValidating(false);
        }
    }, [assetData, validateConnection, isEditMode, editConnection, t]);

    const handleNext = useCallback(async () => {
        // Validate current form before advancing (only for form steps)
        if (activeStep < 3 && formikRef.current) {
            const errors = await formikRef.current.validate();

            // If there are validation errors, don't proceed
            if (Object.keys(errors).length > 0) {
                return;
            }

            const values = formikRef.current.getValues();

            // Server-side validation for connection step
            if (activeStep === 0) {
                const isValid = await validateIoTInput(values);
                if (!isValid) return;
            }

            // Save current form data
            const category = STEP_CATEGORIES[activeStep];
            setFormData((prev) => ({
                ...prev,
                [category]: values,
            }));
        }

        if (activeStep < wizardSteps.length - 1) {
            const nextStep = activeStep + 1;
            setActiveStep(nextStep);
            setHighestStepReached((prev) => Math.max(prev, nextStep));
        }
    }, [activeStep, wizardSteps.length, validateIoTInput]);

    const handleBack = useCallback(() => {
        // Save current form data before going back (no validation needed)
        if (activeStep < 3 && activeStep > 0 && formikRef.current) {
            const values = formikRef.current.getValues();
            const category = STEP_CATEGORIES[activeStep];
            setFormData((prev) => ({
                ...prev,
                [category]: values,
            }));
        }

        if (activeStep > 0) {
            setActiveStep((prev) => prev - 1);
        }
    }, [activeStep]);

    const handleStepClick = useCallback(async (targetStep: number) => {
        // If clicking to a previous step, save current values without validation
        if (targetStep < activeStep) {
            if (activeStep < 3 && formikRef.current) {
                const values = formikRef.current.getValues();
                const category = STEP_CATEGORIES[activeStep];
                setFormData((prev) => ({
                    ...prev,
                    [category]: values,
                }));
            }
            setActiveStep(targetStep);
            return;
        }

        // If clicking to a future step, validate current step first
        if (targetStep > activeStep) {
            if (activeStep < 3 && formikRef.current) {
                const errors = await formikRef.current.validate();

                // If there are validation errors, don't proceed
                if (Object.keys(errors).length > 0) {
                    return;
                }

                const values = formikRef.current.getValues();

                // Server-side validation for connection step
                if (activeStep === 0) {
                    const isValid = await validateIoTInput(values);
                    if (!isValid) return;
                }

                // Save current form data
                const category = STEP_CATEGORIES[activeStep];
                setFormData((prev) => ({
                    ...prev,
                    [category]: values,
                }));
            }
            setActiveStep(targetStep);
            setHighestStepReached((prev) => Math.max(prev, targetStep));
        }
    }, [activeStep, validateIoTInput]);

    useEffect(() => {
        if (validateStepOnMount && formikRef.current) {
            formikRef.current.validate();
            setValidateStepOnMount(false);
        }
    }, [validateStepOnMount, activeStep]);

    const handleFinish = useCallback(async () => {
        if (isSubmitting) return;

        // Backward navigation saves step values without validating, so re-check every
        // saved step here; otherwise invalid data could be submitted via the summary.
        for (let step = 0; step < STEP_CATEGORIES.length; step++) {
            const category = STEP_CATEGORIES[step];
            const schema = generateValidationSchemaFromAttributes(getStepAttributes(category));
            try {
                await schema.validate(formData[category] ?? {}, { abortEarly: false });
            } catch {
                toast.error(t('Please correct the invalid fields before finishing'));
                setActiveStep(step);
                setValidateStepOnMount(true);
                return;
            }
        }

        const timezone = _.get(customerData, 'assetLocation.owner.address.timezone.key', null);
        if (!timezone) {
            const message = canAdministrateCustomers
                ? t('You need to set a timezone for the company to setup an IoT connection.')
                : `${t('You need to set a timezone for the company to setup an IoT connection.')} ${t('Please contact your administrator to set the timezone.')}`;
            toast.error(message);
            return;
        }

        setIsSubmitting(true);

        // Merge all form data + hidden auto-populated fields into a single input object
        const input: Record<string, unknown> = {
            ...formData.connection,
            ...formData.specifications,
            ...formData.serviceData,
            // The telemetry-driven service values are never edited here: create sends
            // them as null, update passes the queried values through verbatim.
            ..._.fromPairs(Object.values(SERVICE_DATA_COUNTERPARTS).map((field) => [
                field,
                isEditMode ? _.get(editConnection, field, null) : null,
            ])),
            assetUrn: _.get(assetData, 'assetLocation.asset.urn', null),
            serialNumber: _.get(assetData, 'assetLocation.asset.serialNumber', null),
            productCode: _.get(assetData, 'assetLocation.asset.product.productCode', null),
            productName: _.get(assetData, 'assetLocation.asset.product.name', null),
            manufacturerName: _.get(assetData, 'assetLocation.asset.product.manufacturer.name', null),
            customerId: _.get(customerData, 'assetLocation.owner.id', null),
            customerName: _.get(customerData, 'assetLocation.owner.name', null),
            location: {
                country: _.get(customerData, 'assetLocation.owner.address.country.countryCode', null),
                region: _.get(customerData, 'assetLocation.owner.region.key', null),
                timezone: _.get(customerData, 'assetLocation.owner.address.timezone.key', null),
            },
        };

        // The backend expects every input field; untouched optionals are sent as null
        OPTIONAL_INPUT_FIELDS.forEach((field) => {
            if (input[field] === undefined || input[field] === '') {
                input[field] = null;
            }
        });

        // A stored gas ballast type can ride along (edit mode) even though the
        // checkbox is off — the type is only valid while gas ballast is enabled.
        if (!isGasBallastEnabled(input)) {
            input.gasBallastType = null;
        }

        try {
            if (isEditMode) {
                const result = await updateIoTConnection({
                    variables: {
                        assetLocationId,
                        input,
                    },
                });

                if (_.get(result, 'data.updateIoTConnection.id')) {
                    toast.success(t('IoT connection updated successfully'));
                    navigate(`/assets/${assetLocationId}/dashboard`);
                } else {
                    toast.error(t('Failed to update IoT connection'));
                    setIsSubmitting(false);
                }
                return;
            }

            const result = await createIoTConnection({
                variables: {
                    assetLocationId,
                    input,
                },
            });

            const connectionId = _.get(result, 'data.createIoTConnection.id');

            if (connectionId) {
                onIoTConnectionCreated?.();
                toast.success(t('IoT connection created successfully'));
                navigate(`/assets/${assetLocationId}/dashboard`);
            } else {
                toast.error(t('Failed to create IoT connection'));
                setIsSubmitting(false);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? resolveErrorMessage(error, t) : t('An unexpected error occurred');
            toast.error(errorMessage);
            setIsSubmitting(false);
        }
    }, [assetLocationId, navigate, formData, assetData, customerData, createIoTConnection, updateIoTConnection, isEditMode, editConnection, isSubmitting, onIoTConnectionCreated, canAdministrateCustomers, getStepAttributes, t]);

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <ConnectionStep
                        assetLocationId={assetLocationId}
                        attributes={connectionAttributes}
                        initialValues={formData.connection}
                        formikRef={formikRef}
                    />
                );
            case 1:
                return (
                    <SpecificationsStep
                        assetLocationId={assetLocationId}
                        attributes={getAttributesForCategory('specifications')}
                        initialValues={formData.specifications}
                        formikRef={formikRef}
                    />
                );
            case 2:
                return (
                    <ServiceDataStep
                        assetLocationId={assetLocationId}
                        attributes={serviceDataAttributes}
                        initialValues={formData.serviceData}
                        formikRef={formikRef}
                    />
                );
            case 3:
                return (
                    <SummaryStep
                        networkScanSummary={networkScanSummary}
                        showCommissioningHint={!isEditMode && isSlaEnabled()}
                        assetLocationId={assetLocationId}
                        formData={formData}
                        attributes={allAttributes.filter(attr => !HIDDEN_ATTRIBUTE_INDICES.includes(attr.index))}
                        onEditStep={handleStepClick}
                    />
                );
            default:
                return null;
        }
    };

    // Pre-checks: only show the wizard when the product allows IoT, all required
    // input data is present and the form definition (TDAs) could be loaded.
    if (assetLoading || customerLoading || systemLoading || tdaLoading || editValuesLoading || iotGate.pending) {
        return <LoadingScreen size="md" />;
    }

    if (assetError || customerError || systemError || iotGate.error || (isEditMode && (editValuesError || !editConnection))) {
        return (
            <ConnectionUnavailableView
                variant="loadFailed"
                assetLocationId={assetLocationId}
                onRetry={() => {
                    if (assetError) refetchAsset();
                    if (customerError) refetchCustomer();
                    if (systemError) refetchSystem();
                    if (iotGate.error) iotGate.refetch();
                    if (isEditMode && (editValuesError || !editConnection)) refetchEditValues();
                }}
            />
        );
    }

    // An asset inside a PLC-type IoT system gets its data via the system's
    // gateway and cannot have its own connection. NON_PLC and unconfigured
    // (null systemType) systems do not block.
    const parentSystem = _.get(systemData, 'assetLocation.system');
    if (!isEditMode && parentSystem && isPlcSystemType(parentSystem.systemType)) {
        return (
            <ConnectionUnavailableView
                variant="partOfIotSystem"
                assetLocationId={assetLocationId}
                systemName={_.get(parentSystem, 'installedBaseNode.title')}
            />
        );
    }

    // Network scan gate (IBM-1039), after the structural partOfIotSystem check:
    // inside a gateway system the asset's flag is always false whatever is
    // documented, so that explanation wins. A false flag with a GREEN scan is
    // structural for another reason and falls through to today's checks.
    if (!isEditMode && iotGate.reason === 'networkScan') {
        return (
            <ConnectionUnavailableView
                variant="networkScan"
                assetLocationId={assetLocationId}
                networkScan={{
                    scan: iotGate.scan,
                    ownNodeId: iotGate.ownNodeId ?? installedBaseNodeId,
                    pathNodeIds: iotGate.pathNodeIds,
                }}
            />
        );
    }

    if (!productAllowsIot) {
        return <ConnectionUnavailableView variant="notAllowed" assetLocationId={assetLocationId} />;
    }

    if (missingRequiredFields.length > 0) {
        return (
            <ConnectionUnavailableView
                variant="missingData"
                assetLocationId={assetLocationId}
                missingFields={missingRequiredFields}
            />
        );
    }

    // Summary line "Network scan OK · documented on …" (create mode, flag on).
    const networkScanSummary = !isEditMode && iotGate.scan && isNetworkScanPassed(iotGate.scan.state)
        ? { sourceName: describeNetworkScanSource(iotGate.scan.source, t), scanDate: iotGate.scan.scanDate ?? '' }
        : undefined;

    if (tdaError || allAttributes.length === 0) {
        return (
            <ConnectionUnavailableView
                variant="loadFailed"
                assetLocationId={assetLocationId}
                onRetry={() => refetchTdas()}
            />
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
            }}
        >
            <Box sx={{ flexShrink: 0 }}>
                <WizardStepper steps={wizardSteps} activeStep={activeStep} highestStepReached={highestStepReached} onStepClick={handleStepClick} />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    py: 2,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        width: '50%',
                        minWidth: 400,
                        maxWidth: 800,
                    }}
                >
                    {renderStepContent()}
                </Box>
            </Box>

            <Box sx={{ pb: 3, flexShrink: 0 }}>
                <WizardNavigation
                    activeStep={activeStep}
                    totalSteps={wizardSteps.length}
                    onBack={handleBack}
                    onNext={handleNext}
                    onFinish={handleFinish}
                    isNextLoading={isValidating}
                    isFinishLoading={isSubmitting}
                    showProgress={false}
                    alignWithStepper
                />
            </Box>
        </Box>
    );
};

export default AssetConnectionWizard;
